import { z } from "zod"

interface SocketRequestArgs<Message> {
  url: URL
  onOpen: () => Promise<unknown>
  schema: z.ZodType<Message, z.ZodTypeDef, unknown>
  /**
   * @param message
   * @returns A boolean indicating whether the operation is done.
   */
  onMessage: (message: Message) => { done: boolean }
  onError: (error: unknown) => void
}

export function socketRequest<Message>(args: SocketRequestArgs<Message>) {
  const { url, onOpen, schema, onMessage, onError } = args

  const websocket = new WebSocket(url)

  websocket.onopen = () =>
    onOpen().catch((error) => {
      onError(error)
      cleanup()
    })

  websocket.onmessage = (event: MessageEvent<string>) => {
    const message = schema.safeParse(JSON.parse(event.data))

    let done
    if (message.success) {
      done = onMessage(message.data).done
    } else {
      done = true
      onError(message.error)
    }

    if (done) cleanup()
  }

  websocket.onerror = () => {
    onError(new Error("The socket connection got interrupted."))
    cleanup()
  }

  const timeout = setTimeout(
    () => {
      onError(new Error("The socket connection timed out."))
      cleanup()
    },
    5 * 60 * 1000,
  ) // 5 minutes.

  function cleanup() {
    clearTimeout(timeout)
    websocket.onopen = null
    websocket.onmessage = null
    websocket.onerror = null
    websocket.close()
  }
}
